import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "f-upload__inner" }
const _hoisted_2 = {
  key: 0,
  class: "f-upload__multiple"
}
const _hoisted_3 = {
  key: 1,
  class: "f-upload__files"
}
const _hoisted_4 = { class: "f-upload__file-name" }
const _hoisted_5 = { class: "f-upload__upbox" }
const _hoisted_6 = { class: "f-upload__icon" }
const _hoisted_7 = {
  key: 1,
  class: "f-upload__img"
}
const _hoisted_8 = ["src", "alt"]

import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import {
  FormUploadEmits,
  FormUploadFile,
  FormUploadProps,
  FormUploadRef,
} from '@/types/form/form-upload';
import SiteRichText from '@/elements/SiteRichText.vue';
import utils from '@/utils';
import { ToastProvider } from '@/types/layout';
import { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';
import { uniqBy } from 'lodash-es';


export default /*@__PURE__*/_defineComponent({
  __name: 'FormUpload',
  props: {
    item: {}
  },
  emits: ["change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;
const formContext = inject<DynamicFormContext>('formContext')!;
const toast = inject<ToastProvider>('toast')!;
const rootEl = ref<HTMLElement>();
const files = ref<FormUploadFile[]>([]);
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const msg = ref<FormElMsgData>();
const fields = computed(() => props.item?.fields);
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const acceptTypes = computed(() => utils.sp.getVals(fields.value?.acceptTypes ?? []) as string[]);
const multiple = computed(() => fields.value?.multiple.value);
const maxSizeLimit = computed(() => fields.value?.maxSize.value ?? 0);
const totalSize = computed(() => files.value.reduce((acc, file) => acc + file.size, 0));
const isValidAcceptTypes = (file: File) => {
  if (!acceptTypes.value || acceptTypes.value.length === 0) return true;
  const extension = utils.file.getExt(file.name);
  return acceptTypes.value.includes(file.type) || acceptTypes.value.includes(`.${extension}`);
};
const onSelectFile = async () => {
  if (!fields.value) return;
  const [selectedFiles, error] = await utils.file.select({
    acceptTypes: acceptTypes.value,
    multiple: multiple.value,
  });
  if (error) {
    toast.showError(error);
    return;
  }
  if (!selectedFiles || selectedFiles.length === 0) return;
  console.log('selectedFiles', selectedFiles);
  const tempFiles: FormUploadFile[] = [];
  for (let file of selectedFiles) {
    if (!isValidAcceptTypes(file)) {
      toast.showError(
        utils.string.ifEmpty(fields.value.invalidTypeMessage.value, `File type is not allowed`)
      );
      return;
    }
    tempFiles.push({
      size: file.size,
      name: file.name,
      type: file.type,
      tempPath: utils.file.getTempPath(file),
      file: file,
    });
  }
  const summarySize = totalSize.value + tempFiles.reduce((acc, file) => acc + file.size, 0);
  if (maxSizeLimit.value > 0 && summarySize > maxSizeLimit.value) {
    toast.showError(`Total file size exceeds the limit of ${maxSizeLimit.value} bytes`);
    return;
  }
  if (multiple.value) {
    let result = [...files.value, ...tempFiles];
    result = uniqBy(result, (result: FormUploadFile) => result.name);
    files.value = result;
  } else {
    files.value = tempFiles;
  }
  emit('change', 'form-upload', fieldName.value, files.value, files.value);
};
const removeFile = async (index: number) => {
  files.value.splice(index, 1);
};
const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (value?: FileList | null) => {
  if (!value) {
    files.value = [];
    return;
  }
  const tempFiles: FormUploadFile[] = [];
  for (let i = 0; i < value.length; i++) {
    tempFiles.push({
      size: value[i].size,
      name: value[i].name,
      type: value[i].type,
      tempPath: utils.file.getTempPath(value[i]),
      file: value[i],
    });
  }
  files.value = tempFiles;
};

const getValue = () => {
  return files.value;
};
const validate = async () => {
  const fileItems: File[] = files.value.map((file) => file.file);
  const value = utils.file.files2FileList(fileItems);
  const result: DynamicFormFieldValidateResult = {
    value,
    valid: true,
    message: '',
  };
  if (!isVisible.value) {
    return result;
  }
  if (isRequired.value && files.value.length === 0) {
    result.valid = false;
    result.message = 'Required field';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  msg.value = undefined;
  return result;
};
const getRefData = () => {
  return {
    el: rootEl.value!,
    type: 'form-input',
    name: fieldName.value,
    id: props.item.id,
    visible: isVisible.value,
    setRequired,
    setDisabled,
    setVisible,
    updateValue,
    getValue,
    validate,
  } as FormUploadRef;
};
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
});
onUnmounted(() => {
  formContext.unRegFormEl(getRefData());
});
__expose(getRefData());

return (_ctx: any,_cache: any) => {
  const _component_icon = _resolveComponent("icon")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["f-upload", { disabled: isDisabled.value, multiple: multiple.value, 'has-error': msg.value?.type === 'error' }]),
        ref_key: "rootEl",
        ref: rootEl
      }, [
        _createElementVNode("div", _hoisted_1, [
          (fields.value.multiple.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (files.value.length === 0)
                  ? (_openBlock(), _createBlock(SiteRichText, {
                      key: 0,
                      field: fields.value.multipleTitle
                    }, null, 8, ["field"]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          class: "f-upload__file",
                          key: index
                        }, [
                          _createElementVNode("span", _hoisted_4, _toDisplayString(file.name), 1),
                          _createVNode(_component_icon, {
                            name: "close",
                            size: "xxs",
                            onClick: ($event: any) => (removeFile(index))
                          }, null, 8, ["onClick"])
                        ]))
                      }), 128))
                    ]))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_5, [
            (multiple.value || files.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "f-upload__dropper",
                  onClick: onSelectFile
                }, [
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_icon, {
                      name: "upload",
                      size: "xs",
                      field: fields.value.icon
                    }, null, 8, ["field"])
                  ]),
                  _createVNode(SiteRichText, {
                    field: fields.value.label,
                    class: "f-upload__label"
                  }, null, 8, ["field"])
                ]))
              : _createCommentVNode("", true),
            (!multiple.value && files.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createElementVNode("div", {
                    class: "f-upload__close",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (files.value = []))
                  }, [
                    _createVNode(_component_icon, { name: "close" })
                  ]),
                  _createElementVNode("img", {
                    src: files.value[0].tempPath,
                    alt: files.value[0].name
                  }, null, 8, _hoisted_8)
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(FormElMsg, { msg: msg.value }, null, 8, ["msg"])
      ], 2))
    : _createCommentVNode("", true)
}
}

})